import React, { useRef, useState, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { MobileVoiceUpload } from 'components/Icons/Icons';

const AudioRecorder = (props) => {
  const {recordedUrl, setRecordedUrl, waveSurferRef, isPlaying, setIsPlaying, waveContainerRef} = props;
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]); // State for recordings list
  const waveformRefs = useRef([]); // Array of refs for waveform instances
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameIdRef = useRef(null);

  useEffect(() => {
    waveSurferRef.current = WaveSurfer.create({
      container: waveContainerRef.current,
      waveColor: '#a0c4ff',
      progressColor: '#007AFF',
      cursorColor: '#ffffff',
      height: 50,
      barWidth: 2,
      responsive: true,
      barGap: 3,
    });

    waveSurferRef.current.on('finish', () => {
      setIsPlaying(false);
    });

    return () => {
      waveSurferRef.current?.destroy();
      cancelAnimationFrame(animationFrameIdRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      setIsRecording(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStream.current = stream;

      mediaRecorder.current = new MediaRecorder(stream);
      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const recordedBlob = new Blob(chunks.current, { type: 'audio/mpeg' });
        const url = URL.createObjectURL(recordedBlob);
        setRecordedUrl(url);

        setRecordings((prevRecordings) => [
          ...prevRecordings,
          { url, isPlaying: false } // Save each recording as an object with a URL and isPlaying state
        ]);
        
        chunks.current = [];
        stopCanvasVisualizer();
      };

      mediaRecorder.current.start();
      visualizeAudio(stream);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
    }
  };

  const initializeWaveform = (containerRef, url) => {
    const waveSurfer = WaveSurfer.create({
      container: containerRef,
      waveColor: '#a0c4ff',
      progressColor: '#007AFF',
      cursorColor: '#ffffff',
      height: 50,
      barWidth: 2,
      responsive: true,
      barGap: 3,
    });

    waveSurfer.load(url);
    return waveSurfer;
  };

  const toggleRecordingPlayback = (index) => {
    const waveSurfer = waveformRefs.current[index];
    const isCurrentlyPlaying = recordings[index].isPlaying;

    if (isCurrentlyPlaying) {
      waveSurfer.pause();
    } else {
      waveSurfer.play();
    }

    // Update the isPlaying state for the specific recording
    setRecordings((prevRecordings) =>
      prevRecordings.map((rec, i) =>
        i === index ? { ...rec, isPlaying: !isCurrentlyPlaying } : rec
      )
    );
  };

  return (
    <div style={{ position: 'relative', zIndex: '111' }}>
      {/* Main waveform for the latest recording */}
      <div
        ref={waveContainerRef}
        style={{
          width: '100%',
          height: '50px',
          borderRadius: '20px',
          backgroundColor: '#007AFF',
          position: 'relative',
          marginBottom: '10px',
        }}
      ></div>

      {/* Canvas for live waveform visualization during recording */}
      <canvas
        ref={canvasRef}
        width="300"
        height="50"
        style={{
          borderRadius: '20px',
          backgroundColor: '#007AFF',
          display: isRecording ? 'block' : 'none',
          marginBottom: '10px',
        }}
      />

      {/* Start/Stop recording button */}
      <MobileVoiceUpload
        h="30px"
        w="30px"
        bg="transparent"
        marginRight="10px"
        zIndex="99"
        onClick={isRecording ? stopRecording : startRecording}
        cursor="pointer"
      />

      {/* List of previous recordings with waveforms */}
      <div style={{ marginTop: '20px' }}>
        <h3>Recordings:</h3>
        {recordings.map((recording, index) => (
          <div key={index} style={{ marginBottom: '20px' }}>
            <div
              ref={(el) => {
                if (el && !waveformRefs.current[index]) {
                  waveformRefs.current[index] = initializeWaveform(el, recording.url);
                }
              }}
              style={{
                width: '100%',
                height: '50px',
                borderRadius: '20px',
                backgroundColor: '#007AFF',
                position: 'relative',
                marginBottom: '10px',
              }}
            ></div>
            <button
              onClick={() => toggleRecordingPlayback(index)}
              style={{
                backgroundColor: 'transparent',
                color: '#007AFF',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
              }}
            >
              {recording.isPlaying ? '⏸ Pause' : '▶️ Play'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AudioRecorder;
